import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { CycleDetailView } from '../components/cycles/CycleDetailView'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { usePageLayout } from '@/hooks/usePageLayout'
import TaskSidebar from '@/components/app/sidebars/task'
import { ProjectIcon } from '@/lib/utils/projectIcon'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/projects/$projectKey/cycles/$cycleId',
)({
  component: CycleDetailsPage,
})

export default function CycleDetailsPage() {
  const { t } = useTranslation('projects')
  const { workspaceSlug, projectKey, cycleId } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const { projects, isLoading } = useProjects()

  const project = useMemo<ProjectDocument | null>(() => {
    return (
      projects.find((p) => {
        if (p.isDeleted) return false
        if (p.projectKey.toLowerCase() !== projectKey.toLowerCase()) return false
        if (workspaceId && p.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [projects, projectKey, workspaceId])

  const breadcrumbContent = useMemo(() => {
    if (!project) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/projects/$projectKey"
          params={{ workspaceSlug, projectKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={14} />
          <span className="truncate">{project.name}</span>
        </Link>
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Link
          to="/$workspaceSlug/projects/$projectKey"
          params={{ workspaceSlug, projectKey }}
          search={{ projectTab: 'cycles' }}
          className="hover:text-foreground transition-colors truncate"
        >
          {t('tabs.cycles')}
        </Link>
      </>
    )
  }, [project, workspaceSlug, projectKey, t])

  usePageLayout({
    sidebarContent: <TaskSidebar />,
    breadcrumb: breadcrumbContent,
  }, [project?.id, project?.name, project?.icon, project?.iconType, project?.iconColor, workspaceSlug, projectKey])

  if (isLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Project not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <CycleDetailView
      cycleId={cycleId}
      projectId={project.id}
      onBack={() => {
        navigate({
          to: '/$workspaceSlug/projects/$projectKey',
          params: { workspaceSlug, projectKey },
        })
      }}
      onComplete={() => {
        navigate({
          to: '/$workspaceSlug/projects/$projectKey',
          params: { workspaceSlug, projectKey },
          search: { projectTab: 'cycles' },
        })
      }}
    />
  )
}
